import {
  hqIncidentCreateRequestSchema,
  hqIncidentUpdateRequestSchema,
  hqIncidentsResponseSchema,
  incidentDetailsResponseSchema,
  type HqIncidentCreateRequest,
  type HqIncidentUpdateRequest,
  type HqIncidentsQuery,
  type IncidentCommentCreateRequest,
} from "@kindtutor/api-contract";
import {
  localizedApiErrorSchema as apiErrorSchema,
  parseApiInput,
  throwApiError,
} from "@kindtutor/auth-client";

import { apiBaseURL, apiFetch } from "../auth-client";

export async function fetchHqIncidents(input: Partial<HqIncidentsQuery> = {}) {
  const query = new URLSearchParams();
  if (input.q) query.set("q", input.q);
  if (input.status) query.set("status", input.status);
  if (input.priority) query.set("priority", input.priority);
  if (input.schoolId) query.set("schoolId", input.schoolId);
  if (input.sort) query.set("sort", input.sort);
  if (input.direction) query.set("direction", input.direction);
  if (input.cursor) query.set("cursor", input.cursor);
  if (input.limit) query.set("limit", String(input.limit));
  const suffix = query.size ? `?${query.toString()}` : "";
  const response = await apiFetch(`${apiBaseURL}/v1/hq/incidents${suffix}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać zgłoszeń.");
  return hqIncidentsResponseSchema.parse(await response.json());
}

export async function fetchHqIncident(incidentId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/incidents/${encodeURIComponent(incidentId)}`,
    { credentials: "include", headers: { Accept: "application/json" } },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać zgłoszenia.");
  return incidentDetailsResponseSchema.parse(await response.json());
}

export async function createHqIncident(values: HqIncidentCreateRequest) {
  const input = parseApiInput(hqIncidentCreateRequestSchema, values);
  const response = await apiFetch(`${apiBaseURL}/v1/hq/incidents`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się utworzyć zgłoszenia.",
    );
  }
  return incidentDetailsResponseSchema.parse(body);
}

export async function updateHqIncident(
  incidentId: string,
  values: HqIncidentUpdateRequest,
) {
  const input = parseApiInput(hqIncidentUpdateRequestSchema, values);
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/incidents/${encodeURIComponent(incidentId)}`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się zaktualizować zgłoszenia.");
  return incidentDetailsResponseSchema.parse(await response.json());
}

export async function addHqIncidentComment(
  incidentId: string,
  input: IncidentCommentCreateRequest,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/incidents/${encodeURIComponent(incidentId)}/comments`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się dodać komentarza.");
  return incidentDetailsResponseSchema.parse(await response.json());
}

export async function uploadHqIncidentAttachment(input: {
  incidentId: string;
  visibility: "school" | "internal";
  file: File;
}) {
  const query = new URLSearchParams({
    filename: input.file.name,
    visibility: input.visibility,
  });
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/incidents/${encodeURIComponent(input.incidentId)}/attachments?${query}`,
    {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json", "Content-Type": input.file.type },
      body: input.file,
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się dodać załącznika.");
  return incidentDetailsResponseSchema.parse(await response.json());
}

export async function downloadHqIncidentAttachment(
  incidentId: string,
  attachmentId: string,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/incidents/${encodeURIComponent(incidentId)}/attachments/${encodeURIComponent(attachmentId)}/content`,
    { credentials: "include", headers: { Accept: "*/*" } },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się pobrać załącznika.");
  const disposition = response.headers.get("Content-Disposition");
  const encoded = disposition?.match(/filename\*=UTF-8''([^;]+)/)?.[1];
  return {
    blob: await response.blob(),
    filename: encoded ? decodeURIComponent(encoded) : "zalacznik",
  };
}

export async function deleteHqIncidentAttachment(
  incidentId: string,
  attachmentId: string,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/incidents/${encodeURIComponent(incidentId)}/attachments/${encodeURIComponent(attachmentId)}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się usunąć załącznika.");
  return incidentDetailsResponseSchema.parse(await response.json());
}
